import { Button, CheckBox, Link, Pill, SystemMessage } from '@idexx-laboratories/spot-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import Grid from '../../components/Grid/Grid';
import Loader from '../../components/Loader/Loader';
import { LoadingSpinnerWithContent } from '../../components/LoadingSpinnerWithContent/LoadingSpinnerWithContent';
import SearchBarExtended from '../../components/SearchBarExtended/SearchBarExtended';
import { useStore } from '../../hooks/use-store';
import { AuditStatus } from '../../types/practice-types';
import './CheckQualifications.scss';
import { hasOverridableQualificationIssue, SubRowRenderer } from './renderers';

const CheckQualifications = observer(() => {
  const { checkQualificationsStore: store, globalStore, globalModalStore, apiStore } = useStore();
  const {
    loaders,
    sapId,
    agentsData,
    agentsDataFiltered,
    expandedRowIds,
    sorting,
    checkedRowsCount,
    upgradeCount,
    noUpgradeCount,
    auditLoading,
    upgradeButtonEnabled,
    searchText,
    hideDoNotUpgrade,
    findComputersPage,
    showInstallationInstructions,
  } = store;
  const { practice } = globalStore;
  const navigate = useNavigate();

  useEffect(() => {
    if (!sapId) {
      void navigate('/');
    } else {
      void store.init();
    }
    return () => store.close();
  }, [navigate, sapId, store]);

  if (loaders.page)
    return (
      <LoadingSpinnerWithContent size='large'>
        One moment while we retrieve your computer information
      </LoadingSpinnerWithContent>
    );

  if (findComputersPage) {
    return (
      <div className='check-qualifications-wrapper'>
        <div className='page-heading skeleton'>
          <div className='spot-typography__heading--level-2'>
            Find my Computers for{' '}
            <span title={globalStore.selectedSap.name}>{globalStore.selectedSap.id}&apos;s</span>{' '}
            {practice?.toCsVersion?.version} upgrade
          </div>
          <div className='spot-typography__text--tertiary'>
            Welcome to My Cornerstone Self-Service Upgrade!
            <br />
            To populate the list of computers please click &quot;Find my computers&quot;.
          </div>
        </div>
        <Button
          className='find-computers-btn'
          variant='primary'
          iconLeft='workstation'
          disabled={globalStore.isLoading}
          onClick={() => void store.findMyComputers()}
          data-testid='find-computers-btn'
        >
          <Loader loading={globalStore.isLoading}>Find my Computers</Loader>
        </Button>
        {showInstallationInstructions && (
          <div className='spot-typography__text--body instructions'>
            <p>
              MyCornerstone uses an application installed alongside Cornerstone to assess
              compatibility with the latest version, and to install the upgrade on your computers.
            </p>
            <p>
              <span className='bold'>
                Thin Client workstations do not need this application since they access Cornerstone
                via Remote Desktop.
              </span>
            </p>
            <p>
              Click
              <Link
                href='#'
                aria-label='kaseya-agent-download'
                onClick={() => store.openKaseyaURL()}
              >
                here
              </Link>
              to download this application and follow the instructions below to complete
              installation.
            </p>
            <p>
              You can also click
              <Link
                href='#'
                aria-label='Send Instructions'
                className={`send-instructions ${loaders.sendInstructions ? 'disabled' : ''}`}
                isDisabled={loaders.sendInstructions}
                onClick={() => void store.sendInstallationInstructions()}
              >
                here
              </Link>
              and MyCornerstone will email <span className='bold'>{apiStore.userEmail}</span> the
              download link and instructions.
            </p>
            <h3>Installation Instructions</h3>
            <ol>
              <li>Use the link on the page above, or in the email to download the application.</li>
              <li>
                Locate the downloaded file on your computer and double click to begin the
                installation.
              </li>
              <li className='no-index'>
                <span className='bold'>Time Saving Tip:</span> Once downloaded, you can copy the
                installation file to a flash drive and take that drive to each computer rather than
                downloading the file each time.
              </li>
              <li>
                If prompted, click <span className='bold'>Yes</span> on the User Account Control
                popup
              </li>
              <li className='no-index'>
                <span className='bold'>Note:</span> During the installation, an Agent Installation
                progress bar will display along with several other windows.
              </li>
              <li>
                After the install completes, click <span className='bold'>OK</span> on the Agent
                Setup message.
              </li>
              <li className='no-index'>
                <span className='bold'>Note:</span> This message only remains on screen for a few
                seconds and may automatically close.
              </li>
              <li>Repeat these steps on each computer your practice uses for Cornerstone.</li>
            </ol>
            <p>
              Once all computers have the application installed, click the{' '}
              <span className='bold'>Find my Computers</span> button above to search for them.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className='check-qualifications-wrapper'>
      <div className='page-heading skeleton'>
        <div className='spot-typography__heading--level-2'>
          {' '}
          Check Qualifications for{' '}
          <span title={globalStore.selectedSap.name}>{globalStore.selectedSap.id}&apos;s</span>{' '}
          {practice?.toCsVersion?.version} upgrade
        </div>
        <div className='horizontal-flex-box'>
          <div>
            <div className='spot-typography__text--tertiary page-sub-header'>
              View your Cornerstone server, workstation connections, and upgrade qualifications.
              Click <span className='bold'>Need Help</span> for more information.
            </div>
            {agentsData.some(a => a.auditStatus === AuditStatus.UNQUALIFIED) && (
              <>
                <div className='spot-typography__text--tertiary spot-typography__font-weight--bold'>
                  QUESTIONS?
                </div>
                <div className='spot-typography__text--tertiary'>
                  CONTACT IDEXX HARDWARE SALES
                  <Link href='tel:+18006680444' iconRight='phone-contact' size='small'>
                    1(800) 668-0444
                  </Link>
                </div>
                {agentsData.some(a => hasOverridableQualificationIssue(a)) && (
                  <div className='spot-typography__text--tertiary'>
                    CONTACT CORNERSTONE SUPPORT
                    <Link href='tel:+18006952877' iconRight='phone-contact' size='small'>
                      1(800) 695-2877
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <div className='card-upgrade-status-wrapper skeleton'>
              <div className='spot-card--primary'>
                <div className='spot-typography__text--secondary spot-typography__font-weight--bold align--center-center'>
                  STATUS
                </div>
                <div className='spot-typography__text--body align--center-center'>
                  <div className='display-status-value-wrapper'>
                    <div className='total-count'>{noUpgradeCount}</div>
                    <Pill color='secondary' size='small' label='Do Not Upgrade' />
                  </div>
                  <div className='display-status-value-wrapper'>
                    <div className='total-count'>{upgradeCount}</div>
                    <Pill color='positive' size='small' label='Upgrade' />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SystemMessage className='check-qualifications-note' alertLevel='warning' icon='bell'>
        Not seeing all your computers? Click{' '}
        <Link
          href='#'
          aria-label='To Find Computers'
          onClick={() => void store.switchToInstallationInstructions()}
        >
          here
        </Link>{' '}
        to access the Find my Computers screen for instructions.
      </SystemMessage>
      {store.isProcessingRow && (
        <SystemMessage className='check-qualifications-note' alertLevel='warning' icon='bell'>
          Qualification checks can take up to an hour. You can keep working while they run, or come
          back later.
        </SystemMessage>
      )}
      <div className='page-top-actions check-qualifications_actions'>
        <div>
          <SearchBarExtended
            className='skeleton'
            aria-label='Label Search'
            disabled={globalStore.isLoading}
            maxLength={15}
            value={searchText}
            placeholder='Search Nickname'
            onSearchClick={v => runInAction(() => (store.searchText = v))}
            data-testid='search-agents'
          />
          <CheckBox
            id='hide-do-not-upgrade'
            label='Hide Do Not Upgrade'
            aria-label='Hide Do Not Upgrade'
            className='hide-do-not-upgrade skeleton'
            disabled={globalStore.isLoading}
            checked={hideDoNotUpgrade}
            onChange={() => runInAction(() => (store.hideDoNotUpgrade = !hideDoNotUpgrade))}
            data-testid='hide-not-upgrade-cb'
          />
        </div>
        <div>
          <div className='get-help-link'>
            <Button
              className='spot-button spot-button--link spot-button--small'
              disabled={globalStore.isLoading}
              onClick={() => store.showNeedHelpModal()}
              data-testid='help-btn'
            >
              Need Help ?
            </Button>
          </div>
          <Button
            className='bold refresh-list-btn skeleton'
            variant='primary'
            size='small'
            onClick={() => void store.fetchAgents()}
            disabled={globalStore.isLoading}
            data-testid='refresh-btn'
          >
            Refresh List
          </Button>
        </div>
      </div>
      <div className='check-qualifications-table'>
        <Grid
          id='check-qualifications-grid'
          loading={
            globalStore.isLoading && (
              <LoadingSpinnerWithContent size='large'>
                One moment while we retrieve your computer information
              </LoadingSpinnerWithContent>
            )
          }
          disabled={globalStore.isLoading}
          columns={store.colDefs}
          data={agentsDataFiltered}
          subrowRenderer={SubRowRenderer}
          expandedRowIds={expandedRowIds}
          sorting={sorting}
          noRowsText={searchText ? `No result were found for "${searchText}"` : 'No data'}
          rowIdField='agentId'
          rowLoadingField='rowLoading'
        />
      </div>
      <div className='page-bottom-actions check-qualifications_bottom-actions'>
        <div>
          <Button
            className='cancel-editing'
            variant='link'
            size='small'
            onClick={() => globalModalStore.showExitUpgradePopup(() => store.exitUpgrade())}
            data-testid='exit-btn'
          >
            Exit Upgrade
          </Button>
          <Button
            className='cancel-editing'
            variant='link'
            size='small'
            onClick={() => void navigate(-1)}
            data-testid='back-btn'
          >
            Back
          </Button>
        </div>
        <div className='main-buttons'>
          <Button
            className='bottom-action-btn skeleton'
            variant='secondary'
            disabled={globalStore.isLoading || auditLoading || checkedRowsCount === 0}
            onClick={() => void store.initiateCheck()}
            data-testid='initiate-audit-btn'
          >
            Initiate Check{checkedRowsCount > 0 && ` - ${checkedRowsCount}`}
          </Button>
          <Button
            className='bottom-action-btn skeleton'
            variant='secondary'
            disabled={globalStore.isLoading || loaders.generatePdf || agentsData.length === 0}
            onClick={() => void store.downloadQualificationPdf()}
            data-testid='download-pdf-btn'
          >
            <Loader loading={loaders.generatePdf}>Qualification Report</Loader>
          </Button>
          <Button
            className='skeleton'
            variant='primary'
            onClick={() => void store.scheduleUpgrade()}
            disabled={globalStore.isLoading || !upgradeButtonEnabled || loaders.confirm}
            data-testid='schedule-date-time-btn'
          >
            <Loader loading={loaders.confirm}>Schedule Date &amp; Time</Loader>
          </Button>
        </div>
      </div>
    </div>
  );
});

export default CheckQualifications;
